import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Play, Pause, Square, Check, Plus, Minus, Clock, Dumbbell, ChevronDown, ChevronUp, Trophy, History } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { formatWeight, displayWeight, inputToKg, getWeightUnit } from '../utils/weightUnit';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AnimatedExThumb = ({ imageStart, imageEnd, name }) => {
  const [showEnd, setShowEnd] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!loaded) return;
    const t = setInterval(() => setShowEnd(p => !p), 900);
    return () => clearInterval(t);
  }, [loaded]);
  if (!imageStart) return <div className="w-full h-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center"><Dumbbell size={16} className="text-gray-400" /></div>;
  return (
    <div className="relative w-full h-full overflow-hidden">
      <img src={imageStart} alt={name} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300" style={{ opacity: showEnd ? 0 : 1 }} onLoad={() => setLoaded(true)} loading="lazy" />
      {imageEnd && <img src={imageEnd} alt="" className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300" style={{ opacity: showEnd ? 1 : 0 }} loading="lazy" />}
    </div>
  );
};

const SessionLogger = ({ token, onNavigate }) => {
  const [tab, setTab] = useState('active');
  const [customLists, setCustomLists] = useState([]);
  const [wizardPlans, setWizardPlans] = useState([]);
  const [selectedSource, setSelectedSource] = useState(null);
  const [sessionExercises, setSessionExercises] = useState([]);
  const [activeExIdx, setActiveExIdx] = useState(0);
  const [timer, setTimer] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [pastSessions, setPastSessions] = useState([]);
  const [expandedSession, setExpandedSession] = useState(null);
  const timerRef = useRef(null);
  const { user } = useAuth();
  const { t } = useTranslation();
  const system = user?.measurement_system || user?.profile?.measurement_system || 'metric';
  const unit = getWeightUnit(system);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchLists();
    fetchSessions();
    return () => clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    if (isRunning) {
      timerRef.current = setInterval(() => setTimer(t => t + 1), 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [isRunning]);

  const fetchLists = async () => {
    try {
      const [listsRes, plansRes] = await Promise.all([
        axios.get(`${API}/exercise/custom-lists`, { headers }),
        axios.get(`${API}/exercise/workout-wizard/plans`, { headers })
      ]);
      setCustomLists(listsRes.data.lists || []);
      setWizardPlans(plansRes.data.plans || []);
    } catch (e) { console.error(e); }
  };

  const fetchSessions = async () => {
    try {
      const res = await axios.get(`${API}/exercise/sessions?days=30`, { headers });
      setPastSessions(res.data.sessions || []);
    } catch (e) { console.error(e); }
  };

  const startFromList = async (listId) => {
    try {
      const res = await axios.get(`${API}/exercise/custom-lists/${listId}`, { headers });
      const list = res.data.list;
      const exs = (list.exercises_detail || []).map(ex => ({
        exercise_id: ex.id, name: ex.name,
        image_start: ex.image_start, image_end: ex.image_end,
        target_sets: ex.sets || 3, target_reps: ex.reps || '10-12',
        sets_completed: [], current_weight: '', current_reps: ''
      }));
      setSessionExercises(exs);
      setSelectedSource({ type: 'list', id: listId, name: list.name });
      setIsRunning(true);
      setTimer(0);
    } catch (e) { console.error(e); }
  };

  const startFromPlan = (plan, dayKey) => {
    const dayData = plan.weekly_schedule[dayKey];
    const exs = (dayData?.exercises || []).map(ex => ({
      exercise_id: ex.exercise_id, name: ex.name,
      image_start: ex.image_start, image_end: ex.image_end,
      target_sets: ex.sets || 3, target_reps: ex.reps || '10-12',
      sets_completed: [], current_weight: '', current_reps: ''
    }));
    setSessionExercises(exs);
    setSelectedSource({ type: 'plan', id: plan.id, name: `${plan.plan_name} - ${dayKey}` });
    setIsRunning(true);
    setTimer(0);
  };

  const logSet = (exIdx) => {
    setSessionExercises(prev => {
      const updated = [...prev];
      const ex = { ...updated[exIdx] };
      const rawWeight = parseFloat(ex.current_weight) || 0;
      const weightInKg = unit === 'lbs' ? inputToKg(rawWeight, 'lbs') : rawWeight;
      ex.sets_completed = [...ex.sets_completed, {
        weight: String(weightInKg),
        reps: ex.current_reps || ex.target_reps,
        timestamp: new Date().toISOString()
      }];
      ex.current_weight = '';
      ex.current_reps = '';
      updated[exIdx] = ex;
      return updated;
    });
  };

  const removeSet = (exIdx, setIdx) => {
    setSessionExercises(prev => {
      const updated = [...prev];
      const ex = { ...updated[exIdx] };
      ex.sets_completed = ex.sets_completed.filter((_, i) => i !== setIdx);
      updated[exIdx] = ex;
      return updated;
    });
  };

  const finishSession = async () => {
    setIsRunning(false);
    try {
      const res = await axios.post(`${API}/exercise/session`, {
        plan_id: selectedSource?.type === 'plan' ? selectedSource.id : null,
        list_id: selectedSource?.type === 'list' ? selectedSource.id : null,
        name: selectedSource?.name || 'Quick Workout',
        exercises: sessionExercises.map(ex => ({
          exercise_id: ex.exercise_id, name: ex.name,
          sets_completed: ex.sets_completed
        })),
        duration_minutes: Math.round(timer / 60),
      }, { headers });
      setSessionComplete(true);
      fetchSessions();
    } catch (e) { console.error(e); }
  };

  const formatTime = (s) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  const totalSetsLogged = sessionExercises.reduce((t, ex) => t + ex.sets_completed.length, 0);

  // Session Complete Screen
  if (sessionComplete) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12" data-testid="session-complete">
        <Trophy size={64} className="mx-auto text-[#FF6600] mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Workout Complete!</h2>
        <p className="text-gray-600 dark:text-gray-300 mb-6">
          {totalSetsLogged} sets logged in {formatTime(timer)}
        </p>
        <div className="flex gap-3 justify-center">
          <button onClick={() => { setSessionComplete(false); setSessionExercises([]); setSelectedSource(null); setTimer(0); }}
            className="bg-[#FF6600] text-white px-6 py-3 rounded-lg font-semibold" data-testid="new-session-btn">
            New Session
          </button>
          <button onClick={() => setTab('history')}
            className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-6 py-3 rounded-lg font-semibold" data-testid="view-history-btn">
            View History
          </button>
        </div>
      </div>
    );
  }

  // Active Workout Session
  if (sessionExercises.length > 0 && selectedSource) {
    const currentEx = sessionExercises[activeExIdx];
    return (
      <div className="max-w-2xl mx-auto pb-8" data-testid="active-session">
        {/* Timer Header */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm mb-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">{selectedSource.name}</p>
            <p className="text-2xl font-mono font-bold text-gray-900 dark:text-white" data-testid="session-timer">{formatTime(timer)}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setIsRunning(!isRunning)} className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700" data-testid="pause-resume-btn">
              {isRunning ? <Pause size={20} className="text-gray-700 dark:text-gray-300" /> : <Play size={20} className="text-[#FF6600]" />}
            </button>
            <button onClick={finishSession} className="bg-red-500 text-white px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-1" data-testid="finish-session-btn">
              <Square size={14} /> Finish
            </button>
          </div>
        </div>

        {/* Exercise Tabs */}
        <div className="flex gap-1 mb-4 overflow-x-auto pb-1">
          {sessionExercises.map((ex, i) => (
            <button key={i} onClick={() => setActiveExIdx(i)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap flex items-center gap-1 ${
                i === activeExIdx ? 'bg-[#FF6600] text-white' : ex.sets_completed.length > 0 ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
              }`} data-testid={`exercise-tab-${i}`}>
              {ex.sets_completed.length > 0 && <Check size={12} />}
              {ex.name.length > 15 ? ex.name.slice(0, 15) + '...' : ex.name}
            </button>
          ))}
        </div>

        {/* Current Exercise */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden mb-4">
          <div className="aspect-video max-h-48">
            <AnimatedExThumb imageStart={currentEx.image_start} imageEnd={currentEx.image_end} name={currentEx.name} />
          </div>
          <div className="p-4">
            <h3 className="font-bold text-lg text-gray-900 dark:text-white">{currentEx.name}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Target: {currentEx.target_sets} sets x {currentEx.target_reps}</p>

            {/* Log Set Input */}
            <div className="flex gap-3 mt-4 items-end">
              <div className="flex-1">
                <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">{t('session.weight')} ({unit})</label>
                <input type="number" value={currentEx.current_weight}
                  onChange={e => {
                    const val = e.target.value;
                    setSessionExercises(prev => { const u = [...prev]; u[activeExIdx] = { ...u[activeExIdx], current_weight: val }; return u; });
                  }}
                  placeholder="0" className="w-full p-3 border-2 border-gray-200 dark:border-gray-700 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-center text-lg font-bold"
                  data-testid="weight-input" />
              </div>
              <div className="flex-1">
                <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Reps</label>
                <input type="text" value={currentEx.current_reps}
                  onChange={e => {
                    const val = e.target.value;
                    setSessionExercises(prev => { const u = [...prev]; u[activeExIdx] = { ...u[activeExIdx], current_reps: val }; return u; });
                  }}
                  placeholder={currentEx.target_reps} className="w-full p-3 border-2 border-gray-200 dark:border-gray-700 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-center text-lg font-bold"
                  data-testid="reps-input" />
              </div>
              <button onClick={() => logSet(activeExIdx)}
                className="bg-[#FF6600] text-white px-5 py-3 rounded-lg font-bold flex items-center gap-1"
                data-testid="log-set-btn">
                <Plus size={16} /> Log
              </button>
            </div>

            {/* Completed Sets */}
            {currentEx.sets_completed.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Completed Sets</p>
                {currentEx.sets_completed.map((set, i) => (
                  <div key={i} className="flex items-center justify-between bg-green-50 dark:bg-green-900/20 p-2.5 rounded-lg" data-testid={`set-logged-${i}`}>
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-xs font-bold">{i + 1}</span>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">{formatWeight(set.weight, unit)} x {set.reps} reps</span>
                    </div>
                    <button onClick={() => removeSet(activeExIdx, i)} className="text-red-400 hover:text-red-600" data-testid={`remove-set-${i}`}>
                      <Minus size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Nav between exercises */}
            <div className="flex justify-between mt-4">
              <button onClick={() => setActiveExIdx(Math.max(0, activeExIdx - 1))} disabled={activeExIdx === 0}
                className="text-sm text-gray-600 dark:text-gray-300 font-semibold disabled:opacity-30" data-testid="prev-exercise-btn">
                Previous
              </button>
              <span className="text-xs text-gray-500 dark:text-gray-400">{activeExIdx + 1} / {sessionExercises.length}</span>
              <button onClick={() => setActiveExIdx(Math.min(sessionExercises.length - 1, activeExIdx + 1))} disabled={activeExIdx === sessionExercises.length - 1}
                className="text-sm text-[#FF6600] font-semibold disabled:opacity-30" data-testid="next-exercise-btn">
                Next Exercise
              </button>
            </div>
          </div>
        </div>

        {/* Session Summary Bar */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm flex justify-around text-center">
          <div>
            <p className="text-lg font-bold text-[#FF6600]">{totalSetsLogged}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Sets</p>
          </div>
          <div>
            <p className="text-lg font-bold text-[#FF6600]">{sessionExercises.filter(e => e.sets_completed.length > 0).length}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Exercises</p>
          </div>
          <div>
            <p className="text-lg font-bold text-[#FF6600]">{formatTime(timer)}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Time</p>
          </div>
        </div>
      </div>
    );
  }

  // Main Screen - Choose what to start
  return (
    <div className="max-w-2xl mx-auto pb-8" data-testid="session-logger">
      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-xl p-1 mb-6">
        <button onClick={() => setTab('active')} className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-colors ${tab === 'active' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-300'}`} data-testid="tab-active">
          Start Workout
        </button>
        <button onClick={() => setTab('history')} className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-colors ${tab === 'history' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-300'}`} data-testid="tab-history">
          History
        </button>
      </div>

      {tab === 'active' && (
        <div className="space-y-4">
          {/* Custom Workout Lists */}
          {customLists.length > 0 && (
            <div>
              <h3 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2"><Dumbbell size={16} className="text-[#FF6600]" /> My Workout Lists</h3>
              <div className="space-y-2">
                {customLists.map(list => (
                  <button key={list.id} onClick={() => startFromList(list.id)}
                    className="w-full bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm text-left hover:shadow-md transition-shadow flex items-center justify-between"
                    data-testid={`start-list-${list.id}`}>
                    <div>
                      <p className="font-bold text-gray-900 dark:text-white">{list.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{list.exercises?.length || 0} exercises</p>
                    </div>
                    <Play size={20} className="text-[#FF6600]" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* AI Generated Plans */}
          {wizardPlans.length > 0 && (
            <div>
              <h3 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2"><Clock size={16} className="text-[#FF6600]" /> AI Workout Plans</h3>
              {wizardPlans.slice(0, 3).map(plan => (
                <div key={plan.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm mb-2 overflow-hidden">
                  <div className="p-4">
                    <p className="font-bold text-gray-900 dark:text-white">{plan.plan_name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{plan.plan_description}</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.keys(plan.weekly_schedule || {}).map(dayKey => (
                        <button key={dayKey} onClick={() => startFromPlan(plan, dayKey)}
                          className="text-xs bg-orange-50 dark:bg-orange-900/20 text-[#FF6600] px-3 py-1.5 rounded-lg font-semibold hover:bg-orange-100 dark:hover:bg-orange-900/40"
                          data-testid={`start-plan-day-${dayKey}`}>
                          <Play size={10} className="inline mr-1" /> {dayKey}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {customLists.length === 0 && wizardPlans.length === 0 && (
            <div className="text-center py-12">
              <Dumbbell size={48} className="mx-auto text-gray-300 dark:text-gray-600 dark:text-gray-300 mb-4" />
              <h3 className="font-bold text-gray-900 dark:text-white mb-2">No Workouts Yet</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">Create a workout plan with our AI Wizard first</p>
              <button onClick={() => onNavigate && onNavigate('workout-wizard')}
                className="bg-[#FF6600] text-white px-6 py-3 rounded-lg font-semibold" data-testid="go-to-wizard-btn">
                Create Workout Plan
              </button>
            </div>
          )}
        </div>
      )}

      {tab === 'history' && (
        <div className="space-y-3" data-testid="session-history">
          {pastSessions.length === 0 ? (
            <div className="text-center py-12">
              <History size={48} className="mx-auto text-gray-300 dark:text-gray-600 dark:text-gray-300 mb-4" />
              <h3 className="font-bold text-gray-900 dark:text-white mb-2">No Sessions Yet</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">Complete a workout to see your history here</p>
            </div>
          ) : (
            pastSessions.map(session => (
              <div key={session.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden" data-testid={`session-${session.id}`}>
                <button onClick={() => setExpandedSession(expandedSession === session.id ? null : session.id)}
                  className="w-full p-4 text-left flex items-center justify-between">
                  <div>
                    <p className="font-bold text-gray-900 dark:text-white">{session.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(session.completed_at).toLocaleDateString()} | {session.duration_minutes} min | {session.exercises?.length || 0} exercises
                    </p>
                  </div>
                  {expandedSession === session.id ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                </button>
                {expandedSession === session.id && (
                  <div className="px-4 pb-4 space-y-2 border-t border-gray-100 dark:border-gray-700 pt-3">
                    {session.exercises?.map((ex, i) => (
                      <div key={i} className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                        <p className="font-semibold text-sm text-gray-900 dark:text-white mb-1">{ex.name}</p>
                        {ex.sets_completed?.map((set, j) => (
                          <p key={j} className="text-xs text-gray-500 dark:text-gray-400">
                            Set {j + 1}: {formatWeight(set.weight, unit)} x {set.reps} reps
                          </p>
                        ))}
                        {(!ex.sets_completed || ex.sets_completed.length === 0) && (
                          <p className="text-xs text-gray-400">No sets recorded</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default SessionLogger;
